import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Chip from '../../Fundamentals/Chip/Chip';
import ComboBox, { POSITIONING_STRATEGY } from '../../Fundamentals/ComboBox';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import FormField from '../../Fundamentals/Form/FormField';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import SkynetIconButton from '../../Fundamentals/IconButton/SkynetIconButton';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import { isValidLength } from './planUtils';
import {
    emptyProductGroupDraft,
    emptyProductGroupTag,
    emptyTierDraft,
    emptyTierTag,
    groupTagTakesRank,
    isValidTierDraft,
    nextSortOrder,
    parseNumberInput,
    tierTagTakesRank,
    toTierDraft,
} from './tierUtils';
import { VossProductGroupTagTypeDescription, VossProductGroupTagTypeText, productGroupTagTypes } from '../../../Constants/VOSS/vossProductGroupTagType';
import { VossProductGroupTypeText, productGroupTypes } from '../../../Constants/VOSS/vossProductGroupType';
import { VossProductTypeText } from '../../../Constants/VOSS/vossProductType';
import { VossRevenueRecognitionBehaviourText, revenueRecognitionBehaviours } from '../../../Constants/VOSS/vossRevenueRecognitionBehaviour';
import { VossTierChangeOperationText, tierChangeOperations } from '../../../Constants/VOSS/vossTierChangeOperation';
import { VossTierTagTypeDescription, VossTierTagTypeText, tierTagTypes } from '../../../Constants/VOSS/vossTierTagType';
import type { PackageTier, ProductGroupDraft, ProductName, TierDraft } from './Packages.types';

const replaceAt = <T,>(items: T[], index: number, change: Partial<T>): T[] => items.map((item, i) => (i === index ? { ...item, ...change } : item));

interface TagChange<T extends number> {
    tag?: string;
    type?: T;
    rank?: number | null;
}

interface TagRowProps<T extends number> {
    id: string;
    value: string;
    type: T;
    typeOptions: T[];
    getTypeLabel: (type: T) => string;
    rank?: { value: number | null; disabled: boolean };
    onChange: (change: TagChange<T>) => void;
    onRemove: () => void;
}

const TagRow = <T extends number>({ id, value, type, typeOptions, getTypeLabel, rank, onChange, onRemove }: TagRowProps<T>) => {
    const { t } = useTranslation();
    const rankInvalid = rank !== undefined && !rank.disabled && rank.value !== null && !isValidLength(rank.value);

    return (
        <div className="d-flex flex-wrap gap-12 align-items-start">
            <FormField
                label={t('packages.tagValue') ?? ''}
                htmlFor={`${id}-value`}
                required
                inline
                help={t('packages.tagValueHelp') ?? ''}
                error={value.trim().length === 0 ? (t('packages.tagValueRequired') ?? '') : undefined}
            >
                <input id={`${id}-value`} type="text" className="form-control" value={value} onChange={(e) => onChange({ tag: e.target.value })} />
            </FormField>
            <FormField label={t('packages.type') ?? ''} htmlFor={`${id}-type`} inline>
                <EnumSelect id={`${id}-type`} value={type} options={typeOptions} getLabel={getTypeLabel} onChange={(next) => onChange({ type: next })} />
            </FormField>
            {rank !== undefined && (
                <FormField label={t('packages.rank') ?? ''} htmlFor={`${id}-rank`} inline error={rankInvalid ? (t('packages.offsetInvalid') ?? '') : undefined}>
                    <input
                        id={`${id}-rank`}
                        type="number"
                        min={0}
                        step={1}
                        className="form-control"
                        disabled={rank.disabled}
                        value={rank.disabled ? '' : (rank.value ?? '')}
                        onChange={(e) => onChange({ rank: parseNumberInput(e.target.value) })}
                    />
                </FormField>
            )}
            {/* labels are 34px tall, so this offset lines the 32px button up with the 32px inputs beside it */}
            <div className="mt-32 pt-2">
                <SkynetIconButton icon="delete" label={t('packages.removeTag')} onClick={onRemove} />
            </div>
        </div>
    );
};

interface ProductGroupSectionProps {
    group: ProductGroupDraft;
    productOptions: { id: string; label: string }[];
    locked: boolean;
    onChange: (group: ProductGroupDraft) => void;
    onRemove: () => void;
}

const ProductGroupSection = ({ group, productOptions, locked, onChange, onRemove }: ProductGroupSectionProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);
    const idPrefix = `tier-group-${group.key}`;
    const update = <K extends keyof ProductGroupDraft>(key: K, value: ProductGroupDraft[K]) => onChange({ ...group, [key]: value });
    const sortOrderInvalid = group.sortOrder !== null && !isValidLength(group.sortOrder);
    const selectedProducts = group.productIds.flatMap((productId) => productOptions.filter((option) => option.id === productId));

    return (
        <div className="border rounded-8 mb-8">
            <button
                type="button"
                className="d-flex align-items-center gap-12 w-100 text-left border-0 bg-transparent p-12 cursorpointer"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <SpirisIcon name={open ? 'chevron-down' : 'chevron-right'} size="sm" className="icon-neutral" />
                <SpirisIcon name="purchasing" size="sm" className="icon-neutral" />
                <span className="flex-1">
                    <span className="font-weight-600 d-block">{group.name.trim().length > 0 ? group.name : t('packages.newProductGroup')}</span>
                    <span className="text-secondary font-size-xs d-block">
                        {t(VossProductGroupTypeText[group.productGroupType])} · {t('packages.productCount', { count: group.productIds.length })}
                    </span>
                </span>
            </button>
            {open && (
                <div className="border-top p-16">
                    <div className="d-flex flex-wrap gap-16">
                        <FormField
                            label={t('common.name') ?? ''}
                            htmlFor={`${idPrefix}-name`}
                            required
                            inline
                            error={group.name.trim().length === 0 ? (t('packages.nameRequired') ?? '') : undefined}
                        >
                            <input id={`${idPrefix}-name`} type="text" className="form-control" value={group.name} onChange={(e) => update('name', e.target.value)} />
                        </FormField>
                        <FormField label={t('packages.externalId') ?? ''} htmlFor={`${idPrefix}-external-id`} inline>
                            <input
                                id={`${idPrefix}-external-id`}
                                type="text"
                                className="form-control"
                                value={group.externalId}
                                onChange={(e) => update('externalId', e.target.value)}
                            />
                        </FormField>
                    </div>
                    <div className="d-flex flex-wrap gap-16">
                        <FormField label={t('packages.type') ?? ''} htmlFor={`${idPrefix}-type`} help={t('packages.productGroupTypeHelp') ?? ''} inline>
                            <EnumSelect
                                id={`${idPrefix}-type`}
                                disabled={locked}
                                value={group.productGroupType}
                                options={productGroupTypes}
                                getLabel={(type) => t(VossProductGroupTypeText[type])}
                                onChange={(type) => update('productGroupType', type)}
                            />
                        </FormField>
                        <FormField
                            label={t('packages.sortOrder') ?? ''}
                            htmlFor={`${idPrefix}-sort-order`}
                            inline
                            error={sortOrderInvalid ? (t('packages.offsetInvalid') ?? '') : undefined}
                        >
                            <input
                                id={`${idPrefix}-sort-order`}
                                type="number"
                                min={0}
                                step={1}
                                className="form-control"
                                value={group.sortOrder ?? ''}
                                onChange={(e) => update('sortOrder', parseNumberInput(e.target.value))}
                            />
                        </FormField>
                    </div>
                    <FormField label={t('packages.revenueRecognition') ?? ''} htmlFor={`${idPrefix}-revenue-recognition`}>
                        <EnumSelect
                            id={`${idPrefix}-revenue-recognition`}
                            disabled={locked}
                            value={group.revenueRecognitionBehaviour}
                            options={revenueRecognitionBehaviours}
                            getLabel={(behaviour) => t(VossRevenueRecognitionBehaviourText[behaviour])}
                            onChange={(behaviour) => update('revenueRecognitionBehaviour', behaviour)}
                        />
                    </FormField>

                    <h5 className="font-weight-600 mb-4">{t('packages.tierTags', { total: group.tags.length })}</h5>
                    <p className="text-secondary font-13 mb-8">{t('packages.productGroupTagsHelp')}</p>
                    {group.tags.map((tag, tagIndex) => (
                        <div key={tagIndex} className="border rounded-8 px-12 pt-8 mb-8">
                            <TagRow
                                id={`${idPrefix}-tag-${tagIndex}`}
                                value={tag.tag}
                                type={tag.type}
                                typeOptions={productGroupTagTypes}
                                getTypeLabel={(type) => t(VossProductGroupTagTypeText[type])}
                                rank={{ value: tag.rank, disabled: !groupTagTakesRank(tag.type) }}
                                onChange={(change) => update('tags', replaceAt(group.tags, tagIndex, change))}
                                onRemove={() =>
                                    update(
                                        'tags',
                                        group.tags.filter((_, i) => i !== tagIndex)
                                    )
                                }
                            />
                            <p className="text-secondary font-13 mb-8">{t(VossProductGroupTagTypeDescription[tag.type])}</p>
                        </div>
                    ))}
                    <button type="button" className="btn btn-tertiary btn-sm mb-16" onClick={() => update('tags', [...group.tags, emptyProductGroupTag()])}>
                        <SpirisIcon name="plus" size="sm" className="mr-8" />
                        {t('packages.addTag')}
                    </button>

                    {/* ComboBox renders its own label, so FormField around it would stack two; chips follow the help text, which FormField can't place */}
                    <div className="form-group">
                        <ComboBox
                            id={`${idPrefix}-products`}
                            label={t('packages.products')}
                            className="mb-8"
                            placeholder={t('packages.searchProducts')}
                            multiPlaceholder={t('packages.searchProducts')}
                            options={productOptions}
                            value={group.productIds}
                            onChange={(values: unknown[]) => update('productIds', values as string[])}
                            valueKey="id"
                            labelKey="label"
                            isMulti
                            hideInternalChips
                            disabled={locked}
                            strategy={POSITIONING_STRATEGY.DIALOG}
                        />
                        <span className="help-block font-size-xs">{t('packages.productsHelp')}</span>
                        {selectedProducts.length > 0 && (
                            <div className="d-flex flex-wrap gap-4 mt-8">
                                {selectedProducts.map((product) => (
                                    <Chip
                                        key={product.id}
                                        label={product.label}
                                        onRemove={
                                            locked
                                                ? undefined
                                                : () =>
                                                      update(
                                                          'productIds',
                                                          group.productIds.filter((productId) => productId !== product.id)
                                                      )
                                        }
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {!locked && (
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn btn-tertiary btn-sm text-error" onClick={onRemove}>
                                <SpirisIcon name="delete" size="sm" className="icon-error mr-8" />
                                {t('packages.removeProductGroup')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface PackageTierEditorDrawerProps {
    tier?: PackageTier;
    initialRank: number;
    limitedEditing: boolean;
    products: ProductName[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (draft: TierDraft) => void;
}

const PackageTierEditorDrawer = ({ tier, initialRank, limitedEditing, products, isSaving, onClose, onSave }: PackageTierEditorDrawerProps) => {
    const { t } = useTranslation();
    const isNew = tier === undefined;
    const [draft, setDraft] = useState<TierDraft>(() => (tier === undefined ? emptyTierDraft(initialRank) : toTierDraft(tier)));

    const update = <K extends keyof TierDraft>(key: K, value: TierDraft[K]) => setDraft((prev) => ({ ...prev, [key]: value }));
    const productOptions = useMemo(
        () =>
            products.map((product) => ({
                id: product.id ?? '',
                label: [product.articleNumber, product.name, product.type === undefined ? undefined : t(VossProductTypeText[product.type])].filter(Boolean).join(' · '),
            })),
        [products, t]
    );
    const rankInvalid = draft.rank === null || !isValidLength(draft.rank);
    const canSave = isValidTierDraft(draft) && !isSaving;

    const toggleOperation = (operation: TierDraft['restrictedChangeOperations'][number]) =>
        update(
            'restrictedChangeOperations',
            draft.restrictedChangeOperations.includes(operation)
                ? draft.restrictedChangeOperations.filter((current) => current !== operation)
                : [...draft.restrictedChangeOperations, operation]
        );

    return (
        <Drawer
            title={(isNew ? t('packages.addTier') : t('packages.editTier')) ?? ''}
            subtitle={t('packages.tierEditorSubtitle') ?? ''}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                    <Button disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button option={ButtonOption.PRIMARY} disabled={!canSave} onClick={() => onSave(draft)}>
                        {isNew ? t('packages.createTier') : t('common.save')}
                    </Button>
                </>
            }
        >
            {limitedEditing && <div className="alert alert-info mb-16">{t('packages.limitedTierEditing')}</div>}
            <div className="d-flex flex-wrap gap-16">
                <FormField
                    label={t('common.name') ?? ''}
                    htmlFor="tier-name"
                    required
                    inline
                    error={draft.name.trim().length === 0 ? (t('packages.nameRequired') ?? '') : undefined}
                >
                    <input id="tier-name" type="text" className="form-control" value={draft.name} onChange={(e) => update('name', e.target.value)} />
                </FormField>
                <FormField label={t('packages.externalId') ?? ''} htmlFor="tier-external-id" help={t('packages.externalIdHelp') ?? ''} inline>
                    <input id="tier-external-id" type="text" className="form-control" value={draft.externalId} onChange={(e) => update('externalId', e.target.value)} />
                </FormField>
                <FormField
                    label={t('packages.rank') ?? ''}
                    htmlFor="tier-rank"
                    required
                    help={t('packages.rankHelp') ?? ''}
                    error={rankInvalid ? (t('packages.offsetInvalid') ?? '') : undefined}
                >
                    <input
                        id="tier-rank"
                        type="number"
                        min={0}
                        step={1}
                        className="form-control"
                        disabled={limitedEditing}
                        value={draft.rank ?? ''}
                        onChange={(e) => update('rank', parseNumberInput(e.target.value))}
                    />
                </FormField>
            </div>

            <h4 className="mt-16 font-16 mb-4">{t('packages.tierTags', { total: draft.tags.length })}</h4>
            <p className="text-secondary font-13 mb-8">{t('packages.tierTagsHelp')}</p>
            {draft.tags.map((tag, index) => (
                <div key={index} className="border rounded-8 px-12 pt-8 mb-8">
                    <TagRow
                        id={`tier-tag-${index}`}
                        value={tag.tag}
                        type={tag.type}
                        typeOptions={tierTagTypes}
                        getTypeLabel={(type) => t(VossTierTagTypeText[type])}
                        rank={{ value: tag.rank, disabled: !tierTagTakesRank(tag.type) }}
                        onChange={(change) => update('tags', replaceAt(draft.tags, index, change))}
                        onRemove={() =>
                            update(
                                'tags',
                                draft.tags.filter((_, i) => i !== index)
                            )
                        }
                    />
                    <p className="text-secondary font-13 mb-8">{t(VossTierTagTypeDescription[tag.type])}</p>
                </div>
            ))}
            <button type="button" className="btn btn-tertiary btn-sm" onClick={() => update('tags', [...draft.tags, emptyTierTag()])}>
                <SpirisIcon name="plus" size="sm" className="mr-8" />
                {t('packages.addTag')}
            </button>

            <h4 id="tier-blocked-operations" className="mt-24 font-16 mb-4">
                {t('packages.blockedOperationsTitle')}
            </h4>
            <p className="text-secondary font-13 mb-8">{t('packages.blockedOperationsHelp')}</p>
            <div className="d-flex flex-wrap gap-16" role="group" aria-labelledby="tier-blocked-operations">
                {tierChangeOperations.map((operation) => (
                    <div key={operation} className="checkbox">
                        <input
                            type="checkbox"
                            id={`tier-blocked-${operation}`}
                            disabled={limitedEditing}
                            checked={draft.restrictedChangeOperations.includes(operation)}
                            onChange={() => toggleOperation(operation)}
                        />
                        <label htmlFor={`tier-blocked-${operation}`}>{t(VossTierChangeOperationText[operation])}</label>
                    </div>
                ))}
            </div>

            <div className="d-flex justify-content-between align-items-center mt-24 mb-8">
                <h4 className="font-16 mb-0">{t('packages.tierProductGroups', { total: draft.productGroups.length })}</h4>
                {!limitedEditing && (
                    <button
                        type="button"
                        className="btn btn-tertiary btn-sm"
                        onClick={() => update('productGroups', [...draft.productGroups, emptyProductGroupDraft(nextSortOrder(draft.productGroups))])}
                    >
                        <SpirisIcon name="plus" size="sm" className="mr-8" />
                        {t('packages.addProductGroup')}
                    </button>
                )}
            </div>
            {draft.productGroups.map((group) => (
                <ProductGroupSection
                    key={group.key}
                    group={group}
                    productOptions={productOptions}
                    locked={limitedEditing}
                    onChange={(changed) =>
                        update(
                            'productGroups',
                            draft.productGroups.map((current) => (current.key === changed.key ? changed : current))
                        )
                    }
                    onRemove={() =>
                        update(
                            'productGroups',
                            draft.productGroups.filter((current) => current.key !== group.key)
                        )
                    }
                />
            ))}
        </Drawer>
    );
};

export default PackageTierEditorDrawer;
